import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useForm } from 'react-hook-form';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardHeader, CardTitle, CardContent } from './Card';
import Button from './Button';
import Input from './Input';
import { X, ChevronDown, ChevronRight, Copy, FileText, Database, Settings } from 'lucide-react';
import api from '../services/api';
import { useAuthStore } from '../store/authStore';

export default function ImprovedCreateProjectModal({ onClose, onCreate }) {
  const { register, handleSubmit, formState: { errors }, setValue } = useForm();
  const { user } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  const [showCopyOptions, setShowCopyOptions] = useState(false);
  const [projects, setProjects] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [copyOptions, setCopyOptions] = useState({
    script: { enabled: false, projectId: null },
    settings: { enabled: false, projectId: null },
    kb: { enabled: false, projectId: null }
  });
  const [companyNames, setCompanyNames] = useState([]);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [selectedCompanyName, setSelectedCompanyName] = useState('');
  const [isNewCompany, setIsNewCompany] = useState(true);
  const companyButtonRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  
  const isAdmin = user?.Role === 'ADMIN';

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    // Extract unique company names
    const uniqueCompanies = [...new Set(projects.map(p => p.Company_Name).filter(Boolean))];
    setCompanyNames(uniqueCompanies);
    
    // For regular users, auto-set Company_Name from existing projects
    if (!isAdmin && projects.length > 0 && projects[0].Company_Name) {
      setSelectedCompanyName(projects[0].Company_Name);
      setValue('company_name', projects[0].Company_Name);
      setIsNewCompany(false);
    }
  }, [projects, isAdmin, setValue]);

  const fetchProjects = async () => {
    try {
      const response = await api.get('/control_panel');
      setProjects(response.data.data || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setProjects([]);
    }
  };

  const filteredProjects = projects.filter(p =>
    (p.Script_Name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleCopyOption = (type) => {
    setCopyOptions(prev => ({
      ...prev,
      [type]: { ...prev[type], enabled: !prev[type].enabled, projectId: null }
    }));
  };

  const selectProject = (type, projectId) => {
    setCopyOptions(prev => ({
      ...prev,
      [type]: { ...prev[type], projectId }
    }));
  };

  const handleCompanySelect = (companyName) => {
    setSelectedCompanyName(companyName);
    setValue('company_name', companyName);
    setIsNewCompany(false);
    setShowCompanyDropdown(false);
  };

  const updateDropdownPosition = () => {
    if (companyButtonRef.current) {
      const rect = companyButtonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  };

  useEffect(() => {
    if (showCompanyDropdown && companyButtonRef.current) {
      updateDropdownPosition();
      window.addEventListener('scroll', updateDropdownPosition, true);
      window.addEventListener('resize', updateDropdownPosition);
      return () => {
        window.removeEventListener('scroll', updateDropdownPosition, true);
        window.removeEventListener('resize', updateDropdownPosition);
      };
    }
  }, [showCompanyDropdown]);

  const handleNewCompany = () => {
    setIsNewCompany(true);
    setSelectedCompanyName('');
    setValue('company_name', '');
  };

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      const newProjectData = {
        Script_Name: data.project_name,
        Company_Name: isAdmin && isNewCompany ? data.company_name : selectedCompanyName || data.company_name || data.project_name,
        Have_Tags: [],
        Not_Have_Tags: [],
        Script: { Settings: {}, Main_Script: { Stages: [] } },
        KB: {},
        MKT: {},
        Sources: {}
      };

      // Копирование данных из выбранных проектов
      for (const [type, option] of Object.entries(copyOptions)) {
        if (option.enabled && option.projectId) {
          const sourceProject = projects.find(p => p.ID === option.projectId);
          if (sourceProject) {
            switch (type) {
              case 'script':
                if (sourceProject.Script) {
                  newProjectData.Script = JSON.parse(JSON.stringify(sourceProject.Script));
                }
                break;
              case 'settings':
                if (sourceProject.Script?.Settings) {
                  newProjectData.Script.Settings = JSON.parse(JSON.stringify(sourceProject.Script.Settings));
                }
                if (sourceProject.Have_Tags) {
                  newProjectData.Have_Tags = [...sourceProject.Have_Tags];
                }
                if (sourceProject.Not_Have_Tags) {
                  newProjectData.Not_Have_Tags = [...sourceProject.Not_Have_Tags];
                }
                break;
              case 'kb':
                if (sourceProject.KB) {
                  const kbCopy = JSON.parse(JSON.stringify(sourceProject.KB));
                  // Normalize legacy lowercase 'items' key to PascalCase 'Items'
                  if (!kbCopy.Items && Array.isArray(kbCopy.items)) {
                    kbCopy.Items = kbCopy.items;
                    delete kbCopy.items;
                  }
                  if (!kbCopy.Items) kbCopy.Items = [];
                  newProjectData.KB = kbCopy;
                }
                if (sourceProject.Basic_Info) {
                  newProjectData.Basic_Info = JSON.parse(JSON.stringify(sourceProject.Basic_Info));
                }
                break;
            }
          }
        }
      }

      await onCreate(newProjectData);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="w-full max-w-2xl"
        style={{ maxHeight: '90vh' }}
      >
        <Card className="bg-white shadow-2xl border-gray-200 flex flex-col" style={{ maxHeight: '90vh' }}>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>Создать новый проект</CardTitle>
              <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Создайте новый проект с нуля или скопируйте компоненты из существующих проектов
            </p>
          </CardHeader>
          <CardContent className="overflow-y-auto flex-1" style={{ overflowX: 'visible' }}>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 pb-4">
              {/* Название проекта */}
              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">Название проекта</label>
                <Input
                  {...register('project_name', { required: 'Название обязательно' })}
                  error={errors.project_name?.message}
                  placeholder="Мой AI ассистент"
                />
              </div>

              {/* Company Name (Admin only) */}
              {isAdmin && (
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">Company Name</label>
                  {isNewCompany ? (
                    <div className="space-y-2">
                      <Input
                        {...register('company_name', { required: 'Company Name обязательно' })}
                        error={errors.company_name?.message}
                        placeholder="Введите новое название компании"
                      />
                      {companyNames.length > 0 && (
                        <button
                          type="button"
                          onClick={() => {
                            setIsNewCompany(false);
                            setShowCompanyDropdown(true);
                          }}
                          className="text-sm text-blue-600 hover:text-blue-700"
                        >
                          Выбрать из существующих
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="relative">
                        <button
                          ref={companyButtonRef}
                          type="button"
                          onClick={() => {
                            setShowCompanyDropdown(!showCompanyDropdown);
                            if (!showCompanyDropdown) updateDropdownPosition();
                          }}
                          className="w-full px-3 py-2 text-left text-sm border border-gray-300 rounded-lg bg-white hover:border-blue-500"
                        >
                          {selectedCompanyName || 'Выберите компанию...'}
                        </button>
                        {showCompanyDropdown && companyNames.length > 0 && createPortal(
                          <div 
                            className="bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto"
                            style={{
                              position: 'fixed',
                              top: `${dropdownPosition.top}px`,
                              left: `${dropdownPosition.left}px`,
                              width: `${dropdownPosition.width}px`,
                              zIndex: 9999
                            }}
                          >
                            {companyNames.map((name, idx) => (
                              <button
                                key={idx}
                                type="button"
                                onClick={() => handleCompanySelect(name)}
                                className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50"
                              >
                                {name}
                              </button>
                            ))}
                          </div>,
                          document.body
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={handleNewCompany}
                        className="text-sm text-blue-600 hover:text-blue-700"
                      >
                        Создать новую компанию
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Опции копирования */}
              <div className="border border-gray-200 rounded-xl" style={{ overflow: 'visible' }}>
                <button
                  type="button"
                  onClick={() => setShowCopyOptions(!showCopyOptions)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Copy size={18} className="text-blue-600" />
                    <span className="font-semibold text-gray-800">Копировать из существующего проекта (опционально)</span>
                  </div>
                  {showCopyOptions ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                </button>

                <AnimatePresence>
                  {showCopyOptions && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      style={{ overflow: 'visible' }}
                    >
                      <div className="p-4 space-y-4 bg-white" style={{ overflow: 'visible' }}>
                        {/* Script */}
                        <CopyOption
                          icon={FileText}
                          label="Скрипт (задачи)"
                          enabled={copyOptions.script.enabled}
                          selectedProjectId={copyOptions.script.projectId}
                          onToggle={() => toggleCopyOption('script')}
                          onSelectProject={(id) => selectProject('script', id)}
                          projects={filteredProjects}
                          searchTerm={searchTerm}
                          onSearchChange={setSearchTerm}
                        />

                        {/* Settings */}
                        <CopyOption
                          icon={Settings}
                          label="Настройки (Bot Role, теги)"
                          enabled={copyOptions.settings.enabled}
                          selectedProjectId={copyOptions.settings.projectId}
                          onToggle={() => toggleCopyOption('settings')}
                          onSelectProject={(id) => selectProject('settings', id)}
                          projects={filteredProjects}
                          searchTerm={searchTerm}
                          onSearchChange={setSearchTerm}
                        />

                        {/* KB */}
                        <CopyOption
                          icon={Database}
                          label="База знаний (KB)"
                          enabled={copyOptions.kb.enabled}
                          selectedProjectId={copyOptions.kb.projectId}
                          onToggle={() => toggleCopyOption('kb')}
                          onSelectProject={(id) => selectProject('kb', id)}
                          projects={filteredProjects}
                          searchTerm={searchTerm}
                          onSearchChange={setSearchTerm}
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Кнопки */}
              <div className="flex space-x-3">
                <Button type="submit" className="flex-1" disabled={isLoading}>
                  {isLoading ? 'Создание...' : 'Создать проект'}
                </Button>
                <Button type="button" variant="outline" onClick={onClose}>
                  Отмена
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}

function CopyOption({ icon: Icon, label, enabled, selectedProjectId, onToggle, onSelectProject, projects }) {
  const [showDropdown, setShowDropdown] = useState(false);
  const selectedProject = projects.find(p => p.ID === selectedProjectId);
  const buttonRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });

  const updatePosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  };

  useEffect(() => {
    if (showDropdown && buttonRef.current) {
      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [showDropdown]);

  return (
    <div className="flex items-center gap-3">
      <label className="flex items-center gap-2 cursor-pointer min-w-[200px]">
        <input
          type="checkbox"
          checked={enabled}
          onChange={onToggle}
          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
        />
        <Icon size={16} className="text-gray-600" />
        <span className="text-sm font-medium text-gray-700">{label}</span>
      </label>

      <div className="flex-1 relative">
        <button
          ref={buttonRef}
          type="button"
          disabled={!enabled}
          onClick={() => {
            setShowDropdown(!showDropdown);
            if (!showDropdown) updatePosition();
          }}
          className={`w-full px-3 py-2 text-left text-sm border rounded-lg transition-all ${
            enabled
              ? 'bg-white border-gray-300 hover:border-blue-500 cursor-pointer'
              : 'bg-gray-100 border-gray-200 cursor-not-allowed text-gray-400'
          }`}
        >
          {selectedProject ? selectedProject.Script_Name : 'Выберите проект...'}
        </button>

        {showDropdown && enabled && createPortal(
          <div 
            className="bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto"
            style={{
              position: 'fixed',
              top: `${dropdownPos.top}px`,
              left: `${dropdownPos.left}px`,
              width: `${dropdownPos.width}px`,
              zIndex: 9999
            }}
          >
            {projects.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">Нет доступных проектов</div>
            ) : (
              projects.map(project => (
                <button
                  key={project.ID}
                  type="button"
                  onClick={() => {
                    onSelectProject(project.ID);
                    setShowDropdown(false);
                  }}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 transition-colors"
                >
                  {project.Script_Name}
                </button>
              ))
            )}
          </div>,
          document.body
        )}
      </div>
    </div>
  );
}
